'use client';

import { useCallback, useState } from 'react';
import { PORTFOLIO_COMPARISON_DEFAULT_PCT } from './portfolio-comparison';

/**
 * Per-item divider positions for a set of before/after comparisons.
 *
 * ONE record for the whole section, keyed by item id, rather than one `useState`
 * inside each comparison. The filmstrip mounts a SECOND `PortfolioComparison`
 * for the item it zooms, and the design calls for the two to agree: a divider
 * dragged in the modal has to be where the visitor left it on the tile behind
 * it, which state owned by either copy could not do. The showcase keeps every
 * item mounted and hides all but one, so its positions would in fact survive
 * locally — it shares the record so both styles keep one mechanism, not because
 * it has no choice.
 *
 * SPARSE ON PURPOSE. An item with no entry reads as the 50% default, so the
 * record starts empty and only ever holds the items a visitor actually dragged.
 * That also means an item list that changes between renders needs no
 * reconciliation — a key that no longer exists is simply never read.
 *
 * `setPct` is `useCallback`-stable and the updater form is not optional: two
 * comparisons dragged in the same frame (a touch on one while another animates)
 * would otherwise write against the same stale snapshot and lose one position.
 */
export const usePortfolioSliders = () => {
  const [pcts, setPcts] = useState<Record<string, number>>({});

  const setPct = useCallback((id: string, pct: number) => {
    setPcts((current) => ({ ...current, [id]: pct }));
  }, []);

  const pctOf = useCallback(
    (id: string) => pcts[id] ?? PORTFOLIO_COMPARISON_DEFAULT_PCT,
    [pcts],
  );

  return { pctOf, setPct };
};
